// Cartas de loot: jogar, escolher depois do boss, descartar e o limite na mão.
import type { Action, GameEvent, LootKind, PlayerId, TerritoryId } from '../types';
import { drawTerritoryCard } from './cards';
import type { Ctx } from './context';
import { addEffect } from './effects';
import { controlsRegion } from '../selectors';

type PlayLoot = Extract<Action, { type: 'playLoot' }>;

function removeFromHand(ctx: Ctx, p: PlayerId, kind: LootKind): void {
  const hand = ctx.s.players[p].loot;
  hand.splice(hand.indexOf(kind), 1);
  ctx.s.lootDiscard.push(kind);
}

/** Tira até `amount` tropas de um território, deixando pelo menos 1. */
function damage(ctx: Ctx, t: TerritoryId, amount: number): number {
  const st = ctx.s.territories[t];
  const lost = Math.max(0, Math.min(amount, st.troops - 1));
  st.troops -= lost;
  return lost;
}

export function playLoot(ctx: Ctx, a: PlayLoot): void {
  const { s, rng } = ctx;
  const p = a.player;
  const rs = s.info.ruleset;
  removeFromHand(ctx, p, a.kind);
  const event: Extract<GameEvent, { type: 'lootPlayed' }> = {
    type: 'lootPlayed',
    player: p,
    kind: a.kind,
    territory: a.territory,
    from: a.from,
    to: a.to,
    count: a.count,
  };
  const turnEnd = { at: 'turnEnd' as const, player: p };
  const nextTurn = { at: 'turnStart' as const, player: p };
  switch (a.kind) {
    case 'strike':
      event.hits = [{ territory: a.territory!, lost: damage(ctx, a.territory!, rs.strikeDamage) }];
      break;
    case 'barrage':
      event.hits = a.targets!.map((t) => ({ territory: t, lost: damage(ctx, t, 1) }));
      break;
    case 'strength':
    case 'precision':
    case 'captain':
      addEffect(ctx, a.kind, p, turnEnd);
      break;
    case 'blessing':
      addEffect(ctx, 'blessing', p, { at: 'used' }, { territory: a.territory });
      break;
    case 'wall':
      addEffect(ctx, 'wall', p, nextTurn, { link: [a.from!, a.to!] });
      break;
    case 'ward':
      addEffect(ctx, 'ward', p, nextTurn, { territory: a.territory });
      break;
    case 'paralyze': {
      const owner = s.territories[a.territory!].owner;
      addEffect(ctx, 'paralyze', p, { at: 'turnEnd', player: owner }, { territory: a.territory });
      break;
    }
    case 'restore':
      s.territories[a.territory!].troops += rs.restoreTroops;
      event.count = rs.restoreTroops;
      break;
    case 'teleport':
      s.territories[a.from!].troops -= a.count!;
      s.territories[a.to!].troops += a.count!;
      s.turn.movedIn[a.to!] = (s.turn.movedIn[a.to!] ?? 0) + a.count!;
      break;
    case 'reinforce':
      s.reinforcements += rs.reinforceLootTroops;
      event.count = rs.reinforceLootTroops;
      break;
    case 'draw': {
      const before = s.players[p].cards.length;
      for (let i = 0; i < rs.drawCards; i++) drawTerritoryCard(ctx, p);
      event.cards = s.players[p].cards.slice(before);
      break;
    }
    case 'steal': {
      const victim = s.players[a.victim!];
      const [card] = victim.cards.splice(rng.int(victim.cards.length), 1);
      s.players[p].cards.push(card);
      event.victim = a.victim;
      event.cards = [card];
      break;
    }
    case 'luck':
      break;
  }
  ctx.events.push(event);
  settleOverflow(ctx, p);
}

export function chooseLoot(ctx: Ctx, p: PlayerId, kind: LootKind): void {
  const { s } = ctx;
  const pending = s.pending;
  if (pending?.kind !== 'lootChoice') return;
  const options = [...pending.options];
  options.splice(options.indexOf(kind), 1);
  s.players[p].loot.push(kind);
  s.lootDiscard.push(...options);
  s.pending = null;
  ctx.events.push({ type: 'lootChosen', player: p, kind });
  checkLootLimit(ctx, p);
}

export function discardLoot(ctx: Ctx, p: PlayerId, kind: LootKind): void {
  removeFromHand(ctx, p, kind);
  ctx.events.push({ type: 'lootDiscarded', player: p, kind });
  settleOverflow(ctx, p);
}

/** Acima do limite: o jogador precisa jogar ou descartar na hora. */
export function checkLootLimit(ctx: Ctx, p: PlayerId): void {
  const { s } = ctx;
  if (s.players[p].loot.length > s.info.ruleset.maxLoot) s.pending = { kind: 'lootOverflow', player: p };
}

function settleOverflow(ctx: Ctx, p: PlayerId): void {
  const { s } = ctx;
  if (s.pending?.kind === 'lootOverflow' && s.players[p].loot.length <= s.info.ruleset.maxLoot) s.pending = null;
}

function drawLoot(ctx: Ctx): LootKind | null {
  const { s, rng } = ctx;
  if (s.lootDeck.length === 0 && s.lootDiscard.length > 0) {
    s.lootDeck = rng.shuffle(s.lootDiscard);
    s.lootDiscard = [];
  }
  return s.lootDeck.shift() ?? null;
}

/**
 * Primeiro a dominar cada região na partida ganha loot (uma vez por região).
 * Só roda sem decisão pendente, para a carta extra nunca atropelar uma escolha em andamento.
 */
export function claimRegions(ctx: Ctx): void {
  const { s } = ctx;
  if (s.pending || s.phase === 'over' || !s.info.ruleset.addons.bosses) return;
  for (const r of s.info.content.map.regions) {
    if (s.regionFirst[r.id] !== undefined) continue;
    const owner = s.players.find((p) => p.alive && controlsRegion(s, p.id, r.id));
    if (!owner) continue;
    s.regionFirst[r.id] = owner.id;
    let last: LootKind | null = null;
    for (let i = 0; i < s.info.ruleset.firstRegionLoot; i++) {
      const card = drawLoot(ctx);
      if (card) owner.loot.push(card);
      last = card ?? last;
    }
    ctx.events.push({ type: 'regionFirst', region: r.id, player: owner.id, loot: last });
    // O limite só é cobrado de quem está jogando (na prática é sempre quem acabou de conquistar).
    if (!s.pending && owner.id === s.current) checkLootLimit(ctx, owner.id);
  }
}
